import { TranscriptEntry } from '../store/conversationSlice';

export interface SpeechRateAnalysis {
  words_per_minute: number;
  is_too_fast: boolean;
  total_words: number;
  time_window: number;
  max_words: number;
  recent_transcripts: TranscriptEntry[];
}

export class SpeechRateService {
  private static instance: SpeechRateService;
  private analysisInterval: NodeJS.Timeout | null = null;

  private constructor() {}

  static getInstance(): SpeechRateService {
    if (!SpeechRateService.instance) {
      SpeechRateService.instance = new SpeechRateService();
    }
    return SpeechRateService.instance;
  }

  /**
   * Count words in a text string
   */
  private countWords(text: string): number {
    if (!text || typeof text !== 'string') return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  /**
   * Get transcripts within the specified time window
   */
  private getTranscriptsInTimeWindow(
    transcripts: TranscriptEntry[],
    timeWindowSeconds: number,
    speaker: 'salesperson' | 'customer' = 'salesperson'
  ): TranscriptEntry[] {
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - (timeWindowSeconds * 1000));

    return transcripts.filter(transcript => {
      const transcriptTime = new Date(transcript.timestamp);
      return transcript.speaker === speaker && transcriptTime >= cutoffTime;
    });
  }

  /**
   * Analyze speech rate for the given transcripts
   */
  analyzeSpeechRate(
    transcripts: TranscriptEntry[],
    timeWindowSeconds: number,
    maxWords: number
  ): SpeechRateAnalysis {
    // Get salesperson transcripts in the time window
    const recentTranscripts = this.getTranscriptsInTimeWindow(
      transcripts,
      timeWindowSeconds,
      'salesperson'
    );

    // Count total words
    const totalWords = recentTranscripts.reduce(
      (total, transcript) => total + this.countWords(transcript.text),
      0
    );

    // Calculate words per minute
    const wordsPerMinute = timeWindowSeconds > 0 
      ? Math.round((totalWords / timeWindowSeconds) * 60)
      : 0;

    // Check if speaking too fast
    const isTooFast = totalWords > maxWords;

    return {
      words_per_minute: wordsPerMinute,
      is_too_fast: isTooFast,
      total_words: totalWords,
      time_window: timeWindowSeconds,
      max_words: maxWords,
      recent_transcripts: recentTranscripts,
    };
  }

  /**
   * Start continuous speech rate monitoring
   */
  startMonitoring(
    getTranscripts: () => TranscriptEntry[],
    getSettings: () => { time_window: number; max_words: number },
    onAnalysis: (analysis: SpeechRateAnalysis) => void,
    intervalMs: number = 2000 // Check every 2 seconds
  ): void {
    this.stopMonitoring(); // Stop any existing monitoring

    this.analysisInterval = setInterval(() => {
      const transcripts = getTranscripts();
      const settings = getSettings();
      
      const analysis = this.analyzeSpeechRate(
        transcripts,
        settings.time_window,
        settings.max_words
      );

      onAnalysis(analysis);
    }, intervalMs);
  }

  /**
   * Stop continuous speech rate monitoring
   */
  stopMonitoring(): void {
    if (this.analysisInterval) {
      clearInterval(this.analysisInterval);
      this.analysisInterval = null;
    }
  }

  /**
   * Get a summary of speech rate for display
   */
  getSpeechRateSummary(analysis: SpeechRateAnalysis): string {
    if (analysis.total_words === 0) {
      return '0 wpm - No speech detected';
    }

    const status = analysis.is_too_fast ? 'Fast' : 'Normal';
    return `${analysis.words_per_minute} wpm - ${status}`;
  }

  /**
   * Get speech rate color for UI
   */
  getSpeechRateColor(analysis: SpeechRateAnalysis): string {
    if (analysis.is_too_fast) {
      return 'bg-red-500';
    }
    return analysis.words_per_minute > 0 ? 'bg-green-500' : 'bg-gray-500';
  }
}

export const speechRateService = SpeechRateService.getInstance(); 